const buildRangeText = ({ currentPage, pageSize, totalItems, rangeStart, rangeEnd }) => {
  if (typeof rangeStart === "number" && typeof rangeEnd === "number") {
    return `${rangeStart}-${rangeEnd} of ${totalItems}`;
  }

  if (!totalItems) {
    return "0-0 of 0";
  }

  const start = (currentPage - 1) * pageSize + 1;
  const end = Math.min(currentPage * pageSize, totalItems);
  return `${start}-${end} of ${totalItems}`;
};

function Pagination({
  currentPage = 1,
  totalPages = 1,
  totalItems = 0,
  pageSize = 10,
  rangeStart,
  rangeEnd,
  onPageChange = () => { },
  align = "start",
}) {
  const safeCurrentPage = Math.min(Math.max(currentPage, 1), Math.max(totalPages, 1));
  const safeTotalPages = Math.max(totalPages, 1);

  const goToPage = (page) => {
    const nextPage = Math.min(Math.max(page, 1), safeTotalPages);
    if (nextPage !== safeCurrentPage) {
      onPageChange(nextPage);
    }
  };

  const buildVisiblePages = () => {
    if (safeTotalPages <= 7) {
      return Array.from({ length: safeTotalPages }, (_, index) => index + 1);
    }

    if (safeCurrentPage <= 4) {
      return [1, 2, 3, 4, 5, "...", safeTotalPages];
    }

    if (safeCurrentPage >= safeTotalPages - 3) {
      return [1, "...", safeTotalPages - 4, safeTotalPages - 3, safeTotalPages - 2, safeTotalPages - 1, safeTotalPages];
    }

    return [1, "...", safeCurrentPage - 1, safeCurrentPage, safeCurrentPage + 1, "...", safeTotalPages];
  };

  const visiblePages = buildVisiblePages();

  const rangeText = buildRangeText({
    currentPage: safeCurrentPage,
    pageSize,
    totalItems,
    rangeStart,
    rangeEnd,
  });

  return (
    <div className={`w-full flex items-center gap-8 ${align === "end" ? "justify-end" : "justify-start"}`}>
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={() => goToPage(safeCurrentPage - 1)}
          disabled={safeCurrentPage <= 1}
          className="w-7 h-7 rounded-full flex items-center justify-center text-titleColor disabled:opacity-40 disabled:cursor-not-allowed"
          aria-label="Previous page"
        >
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path d="M10 4L6 8L10 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>

        {visiblePages.map((page, index) => {
          const isEllipsis = page === "...";
          const isActive = page === safeCurrentPage;

          if (isEllipsis) {
            return (
              <span
                key={`ellipsis-${index}`}
                className="w-[24px] h-[24px] flex items-center justify-center text-[12px] leading-none text-[#5C637A]"
                aria-hidden="true"
              >
                ...
              </span>
            );
          }

          return (
            <button
              key={page}
              type="button"
              onClick={() => goToPage(page)}
              className={`w-[24px] h-[24px] rounded-full flex items-center justify-center text-[12px] leading-none ${isActive ? "bg-[#ECEEF3] text-titleColor" : "text-[#5C637A]"
                }`}
              aria-current={isActive ? "page" : undefined}
              aria-label={`Go to page ${page}`}
            >
              {page}
            </button>
          );
        })}

        <button
          type="button"
          onClick={() => goToPage(safeCurrentPage + 1)}
          disabled={safeCurrentPage >= safeTotalPages}
          className="w-[24px] h-[24px] rounded-full flex items-center justify-center text-titleColor disabled:opacity-40 disabled:cursor-not-allowed"
          aria-label="Next page"
        >
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path d="M6 4L10 8L6 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
      </div>

      <span className="font-OpenSansRegular text-[12px] leading-none text-[#656B7D]">{rangeText}</span>
    </div>
  );
}

export default Pagination;
